import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import BlockRenderer from './BlockRenderer.jsx';
import { newBlock } from './blockSchema.js';
import { useEditor } from './editorStore.js';
import { useEditorTheme } from './ThemeContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { mediaApi } from '../api/media.js';
import { apiError } from '../api/client.js';

const DEVICE_WIDTH = {
  desktop: '100%',
  tablet: '820px',
  mobile: '420px',
};

export default function Canvas() {
  const blocks = useEditor((s) => s.blocks);
  const selectedId = useEditor((s) => s.selectedId);
  const select = useEditor((s) => s.select);
  const device = useEditor((s) => s.device);
  const insertBlock = useEditor((s) => s.insertBlock);
  const theme = useEditorTheme();
  const { workspace } = useAuth();

  const [fileDragging, setFileDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Counter pattern: nested elements fire dragenter/leave individually, so
  // we increment on enter and decrement on leave. The overlay only shows
  // while count > 0.
  const dragCounterRef = useRef(0);
  // Currently-highlighted drop target element. We add `.is-file-drop-target`
  // to it during dragover and clear that on drop / leave / target change.
  const dropTargetRef = useRef(null);

  function isImageDrag(e) {
    const items = e.dataTransfer?.items;
    if (!items || items.length === 0) return false;
    return Array.from(items).some(
      (it) => it.kind === 'file' && (it.type || '').startsWith('image/'),
    );
  }

  // Walk up from elementFromPoint until we hit a container block we can
  // drop into (section / column). Returns { id, el } or null for "drop at
  // page root".
  function findDropContainer(x, y) {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    const target = el.closest('[data-container="true"]');
    if (!target) return null;
    return { id: target.getAttribute('data-block-id'), el: target };
  }

  function setHighlight(nextEl) {
    const cur = dropTargetRef.current;
    if (cur === nextEl) return;
    if (cur) cur.classList.remove('is-file-drop-target');
    if (nextEl) nextEl.classList.add('is-file-drop-target');
    dropTargetRef.current = nextEl;
  }

  function onDragEnter(e) {
    if (!isImageDrag(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setFileDragging(true);
  }

  function onDragLeave(e) {
    if (!isImageDrag(e)) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setFileDragging(false);
      setHighlight(null);
    }
  }

  function onDragOver(e) {
    if (!isImageDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const target = findDropContainer(e.clientX, e.clientY);
    setHighlight(target?.el ?? null);
  }

  async function onDrop(e) {
    if (!isImageDrag(e)) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setFileDragging(false);

    const target = findDropContainer(e.clientX, e.clientY);
    setHighlight(null);

    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      (f.type || '').startsWith('image/'),
    );
    if (!files.length || !workspace) return;

    setUploading(true);
    const toastId = toast.loading(
      files.length === 1
        ? 'Uploading image…'
        : `Uploading 0/${files.length} images…`,
    );
    try {
      const parentId = target?.id ?? '__root__';
      const childCount = childCountOf(blocks, parentId);
      let nextIndex = childCount === -1 ? 0 : childCount;

      const results = await mediaApi.uploadMany(workspace.id, files, {
        onProgress: (done, total) => {
          toast.loading(`Uploading ${done}/${total} images…`, { id: toastId });
        },
        onItem: (media) => {
          if (!media?.url) return;
          const block = newBlock('image');
          block.props.src = media.url;
          block.props.alt = media.alt || '';
          insertBlock(block, parentId, nextIndex++);
        },
      });

      const ok = results.filter((r) => r && !r.error).length;
      const failed = results.length - ok;
      if (failed > 0) {
        toast.error(`${ok} added, ${failed} failed`, { id: toastId });
      } else {
        toast.success(`Added ${ok} image${ok === 1 ? '' : 's'}`, { id: toastId });
      }
    } catch (err) {
      toast.error(apiError(err), { id: toastId });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      className="scroll-pane relative h-full w-full overflow-auto bg-ink-100 p-6 dark:bg-ink-950"
      onClick={() => select(null)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        className="mx-auto overflow-hidden rounded-xl border border-ink-200 bg-white shadow-lg transition-all"
        style={{
          width: DEVICE_WIDTH[device] || '100%',
          minHeight: '70vh',
          fontFamily: theme.fontFamily,
        }}
      >
        <BlockRenderer
          blocks={blocks}
          editable
          selectedId={selectedId}
          onSelect={select}
        />
      </div>
      {(fileDragging || uploading) && (
        <FileDropOverlay uploading={uploading} />
      )}
    </div>
  );
}

function FileDropOverlay({ uploading }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute inset-x-0 top-2 z-30 mx-auto w-fit rounded-2xl border-2 border-dashed bg-white px-4 py-2 shadow-xl backdrop-blur-sm',
        uploading
          ? 'border-ink-200 text-ink-600'
          : 'border-brand-500 text-brand-700',
      )}
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span aria-hidden>{uploading ? '⏳' : '⤓'}</span>
        <span>
          {uploading
            ? 'Uploading…'
            : 'Drop image into the highlighted container (or anywhere for page root)'}
        </span>
      </div>
    </div>
  );
}

// Count the children of a given parent id in a block tree. `__root__`
// returns the top-level block count. Returns -1 if the id isn't found.
function childCountOf(blocks, parentId) {
  if (parentId === '__root__') return blocks.length;
  for (const b of blocks) {
    if (b.id === parentId) return (b.children || []).length;
    if (b.children) {
      const r = childCountOf(b.children, parentId);
      if (r !== -1) return r;
    }
  }
  return -1;
}
